import React, { useEffect, useState } from "react";
import { BookOpen, Zap, Clock, Calendar, Settings, ChevronDown, ChevronUp, BarChart3, Brain, Coffee, TrendingUp, Award, Target, User2, Copy, Link, CheckCheck, LinkIcon, Mail, UserPlus, X } from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import { FaBell, FaClipboardCheck, FaFire, FaGraduationCap, FaSignOutAlt, FaStar, FaTrophy } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const TeacherDashboard = () => {
    const navigate = useNavigate();
    const [teacherData, setTeacherData] = useState(null);
    const [students, setStudents] = useState([]);
    const [activeStudent, setActiveStudent] = useState(null);
    const [showAllStudents, setShowAllStudents] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [studentStats, setStudentStats] = useState({});
    const [recentActivity, setRecentActivity] = useState([]);
    const [inviteCode, setInviteCode] = useState("");
    const [codeCopied, setCodeCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    // Add student by ID modal state
    const [showAddStudent, setShowAddStudent] = useState(false);
    const [studentIdInput, setStudentIdInput] = useState("");
    const [addingStudent, setAddingStudent] = useState(false);
    const [addStudentError, setAddStudentError] = useState("");

    const handleAddStudent = async (e) => {
        e.preventDefault();
        setAddStudentError("");
        if (!studentIdInput.trim()) return;
        setAddingStudent(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/teachers/link-student`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ student_id: studentIdInput.trim() }),
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message);
                setStudents(prev => [...prev, data.student]);
                setStudentIdInput("");
                setShowAddStudent(false);
            } else {
                setAddStudentError(data.message || "Failed to add student.");
            }
        } catch (err) {
            setAddStudentError("Network error. Please try again.");
        } finally {
            setAddingStudent(false);
        }
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    const getGreetingEmoji = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "🌅";
        if (hour < 17) return "☀️";
        return "🌙";
    };

    // Fetch teacher profile and their students
    useEffect(() => {
        const fetchTeacherProfile = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/teachers/me`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const data = await res.json();
                setTeacherData(data.teacher);
                setStudents(data.students || []);
            } catch (err) {
                console.error("Error fetching teacher profile:", err);
            }
        };

        fetchTeacherProfile();
    }, []);

    // Fetch teacher's invite code
    useEffect(() => {
        const fetchInviteInfo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/teachers/invite-info`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                setInviteCode(data.code || "");
            } catch (err) {
                console.error("Error fetching invite info:", err);
            }
        };

        fetchInviteInfo();
    }, []);

    // Fetch selected student's stats
    useEffect(() => {
        if (!activeStudent) return;

        const fetchStudentStats = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/teachers/student-stats/${activeStudent.user_id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const data = await res.json();
                setStudentStats(data.stats);
                setRecentActivity(data.recentActivity);
            } catch (err) {
                console.error("Error fetching student stats:", err);
            }
        };

        fetchStudentStats();
    }, [activeStudent]);

    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const res = await fetch(`${API_BASE_URL}/api/notifications`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (!res.ok) throw new Error("Failed to fetch notifications");

                const data = await res.json();
                const count = data.data.filter(n => n.unread).length;
                setUnreadCount(count);
            } catch (err) {
                console.error("Error fetching notifications:", err);
            }
        };

        fetchUnreadCount();
    }, []);

    const handleNavigate = (path) => navigate(path);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/login";
    };

    const inviteLink = inviteCode
        ? `${window.location.origin}/join-teacher?code=${inviteCode}`
        : "";

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === "code") {
                setCodeCopied(true);
                setTimeout(() => setCodeCopied(false), 2000);
            } else {
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
            }
        } catch {
            // Fallback for older browsers
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
        }
    };

    const displayedStudents = showAllStudents ? students : students.slice(0, 4);

    const studentSelected = (student) => {
        setActiveStudent(student);
        localStorage.setItem("student_id", student.user_id);
        localStorage.setItem("child_id", student.user_id);
        // Mark context so the stats page uses the teacher API endpoints
        localStorage.setItem("viewing_context", "teacher");
        navigate("/student-stats");
    };

    const getScoreColor = (score) => {
        if (score >= 90) return "text-emerald-600 bg-emerald-50";
        if (score >= 80) return "text-green-600 bg-green-50";
        if (score >= 70) return "text-yellow-600 bg-yellow-50";
        if (score >= 60) return "text-orange-600 bg-orange-50";
        return "text-red-600 bg-red-50";
    };

    return (
        <div className="min-h-screen">
            {/* Animated Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
            </div>

            {/* Header */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/20 shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2148C0] to-blue-600 bg-clip-text text-transparent flex items-center">
                                <FaGraduationCap className="mr-3 text-[#2148C0]" />
                                TeeChaa
                            </h1>
                            <div className="hidden md:flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-blue-700 font-medium">Teacher</span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <div className="relative">
                                <FaBell
                                    onClick={() => navigate('/notification')}
                                    className="text-gray-600 text-lg cursor-pointer hover:text-[#2148C0] transition-colors"
                                />
                                {unreadCount > 0 && (
                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                        <span className="text-xs text-white font-bold">{unreadCount}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center space-x-3 bg-white rounded-full p-1 shadow-sm border">
                                <button
                                    onClick={() => handleNavigate('/profile')}
                                    className="text-gray-700 hover:text-[#2148C0] px-3 py-2 rounded-full transition-colors duration-200 font-medium"
                                >
                                    <User2 />
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="text-gray-600 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-all duration-200"
                                >
                                    <FaSignOutAlt />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="relative z-10 container mx-auto px-6 py-8 max-w-7xl">
                {/* Welcome Section */}
                <div className="mb-8 relative">
                    <div className="bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>
                        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-4xl md:text-5xl font-bold mb-3 flex items-center">
                                        {getGreeting()}, {teacherData?.firstName || 'Teacher'} {getGreetingEmoji()}
                                    </h2>
                                    <p className="text-blue-100 text-lg opacity-90">Track and support your students' learning journey</p>
                                </div>
                                <div className="hidden md:block p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                                    <TrendingUp className="w-12 h-12 text-white" />
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-6 text-blue-100">
                                {localStorage.getItem("role_id") && (
                                    <div className="inline-flex items-center gap-3 bg-white/20 border border-white/30 rounded-xl px-4 py-2 backdrop-blur-sm">
                                        <span className="text-xs uppercase tracking-widest text-blue-100">Your ID</span>
                                        <span className="font-mono font-bold text-white text-base tracking-wider">{localStorage.getItem("role_id")}</span>
                                    </div>
                                )}
                                <div className="flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <Clock className="w-4 h-4" />
                                    <span className="font-medium">
                                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <Calendar className="w-4 h-4" />
                                    <span className="font-medium">
                                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <FaGraduationCap className="w-4 h-4" />
                                    <span className="font-medium">{students.length} Student{students.length !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                        </div>
                        <div className="absolute top-4 right-4 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
                        <div className="absolute bottom-4 left-4 w-24 h-24 bg-purple-400/20 rounded-full blur-lg"></div>
                    </div>
                </div>

                {/* ── Quick Actions: Lessons + Questions ───────────────────── */}
                <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-7 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full mr-3"></div>
                        Teach with AI
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                            onClick={() => navigate('/teacher/lessons')}
                            className="text-left bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition"
                        >
                            <BookOpen className="w-6 h-6 text-[#2148C0] mb-2" />
                            <p className="font-semibold text-gray-800">Lessons</p>
                            <p className="text-xs text-gray-400 mt-1">Write lessons in segments (or upload PDFs/docs) with AI practice — free for your students</p>
                        </button>
                        <button
                            onClick={() => navigate('/teacher/marketplace')}
                            className="text-left bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition"
                        >
                            <FaGraduationCap className="w-6 h-6 text-[#2148C0] mb-2" />
                            <p className="font-semibold text-gray-800">Marketplace</p>
                            <p className="text-xs text-gray-400 mt-1">Set a monthly price and list your lessons to sell to subscribers</p>
                        </button>
                        <button
                            onClick={() => navigate('/teacher/questions')}
                            className="text-left bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition"
                        >
                            <Zap className="w-6 h-6 text-[#2148C0] mb-2" />
                            <p className="font-semibold text-gray-800">Question Builder</p>
                            <p className="text-xs text-gray-400 mt-1">Write your own questions with images, or generate drafts with AI</p>
                        </button>
                        <button
                            onClick={() => navigate('/teacher/live')}
                            className="text-left bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition"
                        >
                            <Clock className="w-6 h-6 text-[#2148C0] mb-2" />
                            <p className="font-semibold text-gray-800">Live Sessions</p>
                            <p className="text-xs text-gray-400 mt-1">Schedule live classes and share recordings</p>
                        </button>
                    </div>
                </div>

                {/* ── Invite Link + Add by Student ID ──────────────────────── */}
                <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-7 bg-gradient-to-b from-purple-500 to-blue-500 rounded-full mr-3"></div>
                        Connect Your Students
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Share invite link */}
                        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Invite Link</p>
                            <p className="text-xs text-gray-400 mb-3">Share this link with your student — they click it to join your class</p>

                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-3">
                                <span className="font-mono text-xs text-gray-600 truncate flex-1">{inviteLink || "Loading…"}</span>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => copyToClipboard(inviteCode, "code")}
                                    className="flex items-center gap-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-[#2148C0] font-medium px-3 py-2 rounded-lg transition"
                                >
                                    {codeCopied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {codeCopied ? "Copied!" : "Copy Code"}
                                </button>
                                <button
                                    onClick={() => copyToClipboard(inviteLink, "link")}
                                    className="flex items-center gap-1.5 text-sm bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium px-3 py-2 rounded-lg transition"
                                >
                                    {linkCopied ? <CheckCheck className="w-4 h-4" /> : <Link className="w-4 h-4" />}
                                    {linkCopied ? "Copied!" : "Copy Link"}
                                </button>
                            </div>
                        </div>

                        {/* Add by student ID */}
                        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
                            <div>
                                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Add by Student ID</p>
                                <p className="text-xs text-gray-400 mb-3">Enter your student's TSA ID (found on their account, e.g. TSA-847291)</p>
                            </div>
                            <button
                                onClick={() => { setShowAddStudent(true); setAddStudentError(""); setStudentIdInput(""); }}
                                className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#2148C0] hover:bg-blue-700 text-white font-semibold rounded-xl transition"
                            >
                                <UserPlus className="w-4 h-4" />
                                Add Student
                            </button>
                        </div>
                    </div>
                </div>

                {/* Students Cards */}
                <div className="mb-12">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
                        Your Students
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {displayedStudents.map((student, index) => (
                            <div
                                key={student.user_id || index}
                                onClick={() => studentSelected(student)}
                                className={`group p-6 rounded-2xl cursor-pointer transition-all duration-500 transform hover:scale-105 ${activeStudent?.user_id === student.user_id
                                    ? "bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-blue-400/50 shadow-xl shadow-blue-500/25"
                                    : "bg-white border border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-200"
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div
                                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${activeStudent?.user_id === student.user_id
                                            ? "bg-white/20"
                                            : "bg-gradient-to-br from-[#2148C0] to-blue-500"
                                            }`}
                                    >
                                        {(student.firstName || "S")[0].toUpperCase()}
                                    </div>
                                    {activeStudent?.user_id === student.user_id && (
                                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                                    )}
                                </div>

                                <h3 className={`text-lg font-bold mb-1 transition-colors duration-300 ${activeStudent?.user_id === student.user_id
                                    ? "text-white"
                                    : "text-gray-900 group-hover:text-[#2148C0]"
                                    }`}>
                                    {student.firstName} {student.lastName}
                                </h3>
                                <p className={`text-sm mb-1 ${activeStudent?.user_id === student.user_id ? "text-blue-100" : "text-gray-600"}`}>
                                    {student.school || "—"}
                                </p>
                                <p className={`text-xs font-medium ${activeStudent?.user_id === student.user_id ? "text-blue-200" : "text-gray-500"}`}>
                                    {student.yearGroup || "—"}
                                </p>

                                {student.student_id && (
                                    <p className={`text-xs font-mono mt-2 ${activeStudent?.user_id === student.user_id ? "text-blue-100" : "text-blue-400"}`}>
                                        {student.student_id}
                                    </p>
                                )}

                                {/* Link-type badge */}
                                <div className="mt-3">
                                    {student.linkType === "code" ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                            <LinkIcon className="w-3 h-3" /> Via Code
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                            <Mail className="w-3 h-3" /> Via Email
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}

                        {students.length === 0 && (
                            <div className="col-span-full text-center py-16 bg-white border border-gray-100 rounded-2xl shadow-sm">
                                <FaGraduationCap className="text-5xl text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium mb-1">No students yet</p>
                                <p className="text-gray-400 text-sm">Share your teacher code above to invite students to your class.</p>
                            </div>
                        )}

                        {students.length > 4 && (
                            <div className="col-span-full flex justify-center mt-2">
                                <button
                                    onClick={() => setShowAllStudents(!showAllStudents)}
                                    className="inline-flex items-center text-[#2148C0] border border-[#2148C0] hover:bg-blue-50 transition-all duration-300 px-6 py-3 rounded-xl font-medium text-sm"
                                >
                                    {showAllStudents ? (
                                        <><ChevronUp className="mr-2 w-4 h-4" /> Show Less</>
                                    ) : (
                                        <><ChevronDown className="mr-2 w-4 h-4" /> Show {students.length - 4} More</>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Selected Student Stats (inline preview) */}
                {activeStudent && (
                    <div className="mb-12">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                            <div className="w-2 h-8 bg-gradient-to-b from-green-500 to-blue-500 rounded-full mr-3"></div>
                            {activeStudent.firstName}'s Performance
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-blue-50">
                                        <Clock className="w-6 h-6 text-blue-500" />
                                    </div>
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Time Studied</p>
                                <h3 className="text-3xl font-bold text-gray-900">{studentStats?.total_hours || 0}</h3>
                            </div>

                            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-green-50">
                                        <FaFire className="w-6 h-6 text-green-500" />
                                    </div>
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Study Streak</p>
                                <h3 className="text-3xl font-bold text-gray-900">{studentStats?.streak || 0}<span className="text-lg text-gray-400"> day{(studentStats?.streak || 0) === 1 ? "" : "s"}</span></h3>
                            </div>

                            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-purple-50">
                                        <FaClipboardCheck className="w-6 h-6 text-purple-500" />
                                    </div>
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Exams Completed</p>
                                <h3 className="text-3xl font-bold text-gray-900">{studentStats?.completed_tests || 0}</h3>
                            </div>

                            <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-yellow-50">
                                        <FaTrophy className="w-6 h-6 text-yellow-500" />
                                    </div>
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Average Score</p>
                                <h3 className="text-3xl font-bold text-gray-900">{studentStats?.average_score || 0}<span className="text-lg text-gray-400">%</span></h3>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* ── Add Student Modal ─────────────────────────────────────── */}
            {showAddStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-bold text-gray-800">Add a Student</h3>
                            <button onClick={() => setShowAddStudent(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            Enter the student's <span className="font-semibold text-[#2148C0]">TSA Student ID</span> — they can find it on their profile or the ID card shown when they first registered.
                        </p>

                        <form onSubmit={handleAddStudent} className="space-y-4">
                            <input
                                type="text"
                                value={studentIdInput}
                                onChange={e => { setStudentIdInput(e.target.value); setAddStudentError(""); }}
                                placeholder="e.g. TSA-847291"
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#2148C0]"
                                autoFocus
                            />

                            {addStudentError && (
                                <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{addStudentError}</p>
                            )}

                            <button
                                type="submit"
                                disabled={addingStudent || !studentIdInput.trim()}
                                className="w-full py-3 bg-[#2148C0] hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl transition flex items-center justify-center gap-2"
                            >
                                {addingStudent ? (
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                ) : (
                                    <><UserPlus className="w-4 h-4" /> Add Student</>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
};

export default TeacherDashboard;
